import { useState, useEffect } from 'react';
import { Table, TableHeader, TableBody, Column, Row } from 'react-aria-components';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getChildren } from '@/api/getChildren';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { CreateChildDialog } from '@/components/children/CreateChildDialog';
import { FilterDialog } from '@/components/children/FilterDialog';
import InputText from '@/components/InputText/InputText';
import { Pagination } from '@/components/Pagination/Pagination';
import { UserAtom } from '@/jotai/jotai';
import { Child, ChildrenSearchParams } from '@/types/child';

const defaultOffset = 0;
const defaultLimit = 15;

type SearchInput = {
  word: string;
  recipientCertificateEndDateFrom?: string;
  recipientCertificateEndDateTo?: string;
  supportEndDateFrom?: string;
  supportEndDateTo?: string;
  hasUpperManagement?: boolean;
  schoolGrade?: string;
  schoolName?: string;
  city?: string;
};

export const ChildrenPage = () => {
  const { register, handleSubmit } = useForm<SearchInput>();
  const [children, setChildren] = useState<Child[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isOpenFilterDialog, setIsOpenFilterDialog] = useState(false);
  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);

  const [loginUser] = useAtom(UserAtom);

  const navigate = useNavigate();

  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '利用者情報一覧' }];

  useEffect(() => {
    const fetchChildren = async () => {
      const searchParams: ChildrenSearchParams = {
        limit: defaultLimit.toString(),
        offset: defaultOffset.toString(),
      };

      const result = await getChildren(searchParams, loginUser?.email);
      if (result.status === 200) {
        setChildren(result.result.children);
        setTotalPages(
          result.result.total === 0 ? 1 : Math.ceil(result.result.total / defaultLimit)
        );
      }
    };
    fetchChildren();
  }, []);

  const onSubmit: SubmitHandler<SearchInput> = async (data) => {
    const searchParams: ChildrenSearchParams = {
      limit: defaultLimit.toString(),
      offset: defaultOffset.toString(),
      word: data.word,
      recipientCertificateEndDateFrom: data.recipientCertificateEndDateFrom,
      recipientCertificateEndDateTo: data.recipientCertificateEndDateTo,
      supportEndDateFrom: data.supportEndDateFrom,
      supportEndDateTo: data.supportEndDateTo,
      hasUpperManagement: data.hasUpperManagement,
      schoolGrade: data.schoolGrade,
      schoolName: data.schoolName,
      city: data.city,
    };
    const result = await getChildren(searchParams, loginUser?.email);
    if (result.status === 200) {
      setChildren(result.result.children);
      setTotalPages(result.result.total === 0 ? 1 : Math.ceil(result.result.total / defaultLimit));
    } else {
      console.log('error');
    }
  };

  const navigateToChildEdit = (childID: number) => {
    navigate(`/admin/children/${childID}`);
  };

  const handlePageChange = async (newPage: number) => {
    setCurrentPage(newPage);

    const offset = (newPage - 1) * defaultLimit;
    const searchParams: ChildrenSearchParams = {
      limit: defaultLimit.toString(),
      offset: offset.toString(),
    };
    const result = await getChildren(searchParams, loginUser?.email);
    if (result.status === 200) {
      setChildren(result.result.children);
    } else {
      console.log('error');
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="mb-4 flex items-center justify-between bg-[#F2F2F266] px-5">
        <div className="flex flex-row items-center space-x-2">
          <InputText
            name="word"
            type="word"
            required={false}
            register={register('word')}
          />
          <Button
            size="lg"
            type="submit"
            variant="solid-fill"
            onClick={handleSubmit(onSubmit)}
          >
            検索
          </Button>
          <Button
            size="lg"
            type="submit"
            variant="solid-fill"
            onClick={() => setIsOpenFilterDialog(true)}
          >
            フィルタ
          </Button>
        </div>
        <div className="flex-shrink-0">
          <Button
            type="submit"
            size="lg"
            variant="solid-fill"
            onClick={() => setIsOpenCreateDialog(true)}
          >
            新規登録
          </Button>
        </div>
      </div>
      {isOpenFilterDialog && (
        <FilterDialog
          isOpen={isOpenFilterDialog}
          setIsOpen={setIsOpenFilterDialog}
          afterSubmit={handleSubmit(onSubmit)}
        />
      )}
      {isOpenCreateDialog && (
        <CreateChildDialog
          isOpen={isOpenCreateDialog}
          setIsOpen={setIsOpenCreateDialog}
          afterSubmit={handleSubmit(onSubmit)}
        />
      )}

      <Table className="w-full border-collapse border border-gray-200">
        <TableHeader>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            No
          </Column>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            管理番号
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            利用者(児童)
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            保護者
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            メールアドレス
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            連絡先TEL
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            学校/学年
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            上限管理
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            市区町村
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            受給者証期限
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            支援計画期限
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"></Column>
        </TableHeader>
        <TableBody>
          {children.map((child, index) => (
            <Row key={child.id}>
              <Column
                key="no"
                className="border border-gray-200 px-4 py-2"
              >
                {index + defaultLimit * (currentPage - 1) + 1}
              </Column>
              <Column
                key="id"
                className="border border-gray-200 px-4 py-2"
              >
                {child.id}
              </Column>
              <Column
                key="childName"
                className="border border-gray-200 px-4 py-2"
              >{`${child.last_name}${child.first_name}`}</Column>
              <Column
                key="guardianName"
                className="border border-gray-200 px-4 py-2"
              >{`${child.guardians[0].last_name}${child.guardians[0].first_name}`}</Column>
              <Column
                key="email"
                className="border border-gray-200 px-4 py-2"
              >
                {child.email}
              </Column>
              <Column
                key="phoneNumber"
                className="border border-gray-200 px-4 py-2"
              >
                {child.phone_number}
              </Column>
              <Column
                key="school"
                className="border border-gray-200 px-4 py-2"
              >{`${child.school_name}/${child.school_grade}`}</Column>
              <Column
                key="upperManagement"
                className="border border-gray-200 px-4 py-2"
              >
                {child.has_upper_management ? 'あり' : 'なし'}
              </Column>
              <Column
                key="city"
                className="border border-gray-200 px-4 py-2"
              >
                {child.city || ''}
              </Column>
              <Column
                key="recipientCertEndDate"
                className="border border-gray-200 px-4 py-2"
              >
                {child.recipient_certificates?.[0]?.end_date || ''}
              </Column>
              {/* TODO: 支援計画期限は支援計画実装時に実際の値を入れる */}
              <Column
                key="supportPlanEndDate"
                className="border border-gray-200 px-4 py-2"
              >
                {''}
              </Column>
              <Column
                key="edit"
                className="border border-gray-200 px-4 py-2"
              >
                <Button
                  size="sm"
                  variant="text"
                  type="submit"
                  onClick={() => navigateToChildEdit(child.id)}
                >
                  編集
                </Button>
              </Column>
            </Row>
          ))}
        </TableBody>
      </Table>
      <div className="flex items-center justify-center gap-4 py-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};
